import { pixelRect } from '@/utils/tilePixels';
import { createAdapterLabelAction } from '@/actions/adapterLabelActions';
import { addTileArtwork, clearTileArtwork } from '@/utils/tileArtwork';
import { CircuitType, Side } from '@/schemas/common';
import { TileType } from '../TileTypes';
import {
  ConnectionFlags,
  ContextMenuActionFn,
  CreateActionsFn,
  CreateContextMenuItemsData,
  CreateDefaultStateData,
  TileDefinition,
  TileGraphicsContext,
  TileGraphicsDefinition,
  TileMeta,
  TileSideIO,
} from './TileDefinition';
import { InterfaceTileState } from '@/schemas/tiles';
import { IChipEditorModel } from '@/interfaces/models';
import { Graphics, Text } from 'pixi.js';
import { TileInstance } from './TileInstance';
import { addInterfaceTileToChipModel } from '@/actions/tileActions';
import { InterfaceLink, InterfaceType, SIInputMode } from '../ChipModel';
import { InterfaceLinkInstanceId } from '@/types/ids';
import { Rotation, rotationToRadians } from '../data/Rotation';
import { allocateInterfaceLabel } from './interfaceLabelAllocation';

export abstract class InterfaceTileDefinition<TState = null>
  implements TileDefinition<InterfaceTileState<TState>>
{
  abstract type: TileType;
  abstract name: string;
  abstract canRotate: boolean;
  abstract ioSides: Record<Side, TileSideIO>;
  abstract graphics: TileGraphicsDefinition<InterfaceTileState<TState>>;
  abstract createActions?: CreateActionsFn<InterfaceTileState<TState>> | undefined;
  isInterface = true;
  protected get interfaceType(): InterfaceType {
    return InterfaceType.SINGLE;
  }

  get eventHandlers() {
    const interfaceType = this.interfaceType;
    return {
      placed: (data: {
        instance: TileInstance<InterfaceTileState<TState>>;
        chipEditorModel: IChipEditorModel;
        interfaceLabel?: string;
      }) => {
        const { instance, chipEditorModel } = data;
        const existingLinkId = instance.state.connectedInterface.interfaceLinkId as
          | InterfaceLinkInstanceId
          | '';
        const existingLink = existingLinkId
          ? chipEditorModel.chipModel.interfaceLinkMap.get(existingLinkId)
          : undefined;

        if (existingLink) {
          addInterfaceTileToChipModel(instance, chipEditorModel);
          return;
        }

        // A preview suggests a number, never a link to an already occupied pin.
        const label = allocateInterfaceLabel(
          chipEditorModel.chipModel,
          data.interfaceLabel,
        );
        const resolvedLink = chipEditorModel.chipModel.createInterfaceLink(
          interfaceType,
          label,
        );
        instance.state.connectedInterface.interfaceLinkId = resolvedLink.id;

        // Sync inputMode from tile state to the InterfaceLink
        const tileInputMode = (instance.state as { inputMode?: SIInputMode }).inputMode;
        if (tileInputMode && tileInputMode !== SIInputMode.NONE) {
          resolvedLink.inputMode = tileInputMode;
        }

        addInterfaceTileToChipModel(instance, chipEditorModel);
      },
    };
  }

  createContextMenuItems({
    instance,
    chipEditorModel,
  }: CreateContextMenuItemsData<InterfaceTileState<TState>>): Array<{
    label: string;
    action: ContextMenuActionFn;
  }> {
    const isMulti = this.interfaceType === InterfaceType.MULTI;
    return [
      {
        label: 'Rename',
        action: (_takeInput, uiModel) => {
          const linkId = instance.state.connectedInterface
            .interfaceLinkId as InterfaceLinkInstanceId;
          const link = chipEditorModel.chipModel.interfaceLinkMap.get(linkId);
          uiModel
            .openInterfaceRenameDialog(link?.label ?? '', link?.sideLabels ?? {}, isMulti)
            .then((result) => {
              if (chipEditorModel.chipModel.kind === 'bundle-adapter') {
                chipEditorModel.currentCircuitEditor.runAction(
                  createAdapterLabelAction(
                    chipEditorModel.chipModel,
                    linkId,
                    result.label,
                  ),
                  'Set signal label',
                );
              } else chipEditorModel.chipModel.renameInterfaceLink(linkId, result.label);
              if (link) {
                link.sideLabels = result.sideLabels as Partial<Record<Side, string>>;
                invalidateInterfaceLinkTiles(link);
              }
            })
            .catch(() => {});
        },
      },
    ];
  }

  createDefaultState({
    globalTilePosition,
    meta,
  }: CreateDefaultStateData): InterfaceTileState<TState> {
    return {
      connectedInterface: {
        chipInstanceId: meta.partOfChip ?? globalTilePosition.chipInstanceId,
        circuitType: meta.partOfChip
          ? // If this is part of a placed chip, the connected interface is in the logic graph of the placed chip
            CircuitType.ChipLogic
          : // If this is not part of a placed chip, the connected interface is in the other circuit of the chip we are editing
            globalTilePosition.circuitType === CircuitType.ChipLogic
            ? CircuitType.ChipStructure
            : CircuitType.ChipLogic,
        interfaceLinkId: '',
      },
    } as InterfaceTileState<TState>;
  }
}

export const invalidateInterfaceLinkTiles = (link: InterfaceLink): void => {
  for (const tile of Object.values(link.tiles)) {
    if (tile) {
      tile.renderVersion++;
    }
  }
};

export const getInterfaceLinkFromTile = (
  chipEditorModel: IChipEditorModel | undefined,
  meta: TileMeta,
  state: InterfaceTileState<any>,
  context?: TileGraphicsContext,
): InterfaceLink | undefined => {
  if (context?.interfaceLink) return context.interfaceLink;
  if (!chipEditorModel) return undefined;
  const linkId = state.connectedInterface.interfaceLinkId as InterfaceLinkInstanceId;
  if (!linkId) return undefined;

  if (meta.partOfChip) {
    // Check if we are placing a chip (preview)
    if (
      context?.editor?.placeChipModel.selectedChip?.editingInstance.id === meta.partOfChip
    ) {
      return context.editor.placeChipModel.selectedChip.interfaceLinkMap.get(linkId);
    }

    let chipInstance = chipEditorModel.logicCircuitEditor.circuitModel.chips.find(
      (c) => c.id === meta.partOfChip,
    );
    if (!chipInstance) {
      chipInstance = chipEditorModel.structureCircuitEditor.circuitModel.chips.find(
        (c) => c.id === meta.partOfChip,
      );
    }

    if (chipInstance?.chip) {
      return chipInstance.chip.interfaceLinkMap.get(linkId);
    }
  }

  return chipEditorModel.chipModel.interfaceLinkMap.get(linkId);
};

export const drawInterfaceTileGraphics = <TState>(
  g: Graphics,
  {
    meta,
    state,
    context,
    chipEditorModel,
  }: {
    meta: TileMeta;
    state: InterfaceTileState<TState>;
    connections: ConnectionFlags;
    chipEditorModel?: IChipEditorModel;
    context?: TileGraphicsContext;
  },
) => {
  const tileSize = 120;
  const center = tileSize / 2;

  clearTileArtwork(g);

  const link = getInterfaceLinkFromTile(chipEditorModel, meta, state, context);

  const labelHidden = link?.labelHidden ?? false;
  const isHovered = context?.hoveredTileId !== undefined;
  const showLabel = context?.showLabels !== false && (!labelHidden || isHovered);

  if (showLabel) {
    const labelText = new Text({
      text: link?.label ?? '',
      style: {
        fill: 0xffffff,
        fontSize:
          link?.inputMode === SIInputMode.BUTTON && link.label.length <= 2 ? 56 : 20,
        align: 'center',
        wordWrap: true,
        wordWrapWidth: tileSize - 10,
      },
    });

    labelText.anchor.set(0.5);
    labelText.x = center;
    labelText.y = center;
    // Counter-rotate label so it stays upright regardless of tile rotation
    labelText.rotation = -rotationToRadians((meta.rotation as Rotation) ?? Rotation.CW_0);
    addTileArtwork(g, labelText);
  }

  drawInterfaceCompletenessBorder(g, {
    meta,
    state,
    context,
    chipEditorModel,
  });
};

/** Editor-only pairing cue in the outer gutter, leaving all four ports open. */
export const drawInterfaceCompletenessBorder = <TState>(
  g: Graphics,
  {
    meta,
    state,
    context,
    chipEditorModel,
  }: {
    meta: TileMeta;
    state: InterfaceTileState<TState>;
    chipEditorModel?: IChipEditorModel;
    context?: TileGraphicsContext;
  },
) => {
  if (
    context?.isTileImageGeneration ||
    !chipEditorModel ||
    meta.partOfChip ||
    chipEditorModel.chipModel.kind === 'bundle-adapter'
  )
    return;

  const link = getInterfaceLinkFromTile(chipEditorModel, meta, state, context);
  if (link?.tiles[state.connectedInterface.circuitType] !== undefined) return;

  // One muted logical pixel, outside artwork at 1..23. The gaps retain the
  // independent port/connection colors at 8..16; selection remains blue.
  const color = 0x686868;
  for (const start of [0, 17]) {
    pixelRect(g, start, 0, 8, 1, color);
    pixelRect(g, start, 24, 8, 1, color);
    pixelRect(g, 0, start, 1, 8, color);
    pixelRect(g, 24, start, 1, 8, color);
  }
};
